/**
 * Enhanced Conversions for Web ("Erweiterte Conversions") — Google-tag method.
 *
 * This is Google's "Method 3: Code-Snippet auf der Website einfügen": we hand
 * the tag the user-provided data ourselves instead of letting it guess at the
 * DOM. That is the accurate option — the automatic CSS/DOM scrape misses every
 * field on this site, because all our lead forms unmount (modal close, React
 * state reset) the moment the conversion fires, so there is nothing left on the
 * page for the tag to read.
 *
 * Two hard rules this module enforces, both of which the Ads UI cannot:
 *
 * 1. **Marketing consent, not analytics consent.** User-provided data is
 *    `ad_user_data` under Consent Mode v2. Sending it on an analytics-only
 *    consent would be a DSGVO problem *and* would be dropped by Google anyway.
 * 2. **Nothing leaves the browser in plaintext.** Values are normalised to
 *    Google's spec and SHA-256 hashed locally via Web Crypto, so the tag
 *    transmits `sha256_*` digests. (gtag.js hashes unhashed input itself, but
 *    doing it here keeps the plaintext out of `dataLayer`, out of any tag
 *    debugger, and makes the DSGVO documentation trivial.)
 *
 * Setup that is NOT code and has to be done once in the Google Ads UI:
 *   - Conversion action → "Erweiterte Conversions für das Web aktivieren"
 *   - Method: "Google-Tag"
 *   - Google-Tag → Einstellungen → "Funktionen für von Nutzern bereitgestellte
 *     Daten zulassen" must be ON.
 */

import { readStoredConsent } from "./ga4Consent";

/** Country code assumed for national-format phone numbers (Germany). */
const DEFAULT_PHONE_COUNTRY_CODE = "49";

/** ISO 3166-1 alpha-2 assumed when a form collects an address but no country. */
const DEFAULT_COUNTRY = "DE";

export interface EnhancedConversionInput {
  email?: string;
  phone?: string;
  /** Single free-text name field, e.g. "Max Mustermann". Split on whitespace. */
  name?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  /** State / Bundesland. */
  region?: string;
  postalCode?: string;
  /** ISO 3166-1 alpha-2; defaults to `DE` when any other address part is set. */
  country?: string;
}

interface UserDataAddress {
  sha256_first_name?: string;
  sha256_last_name?: string;
  first_name?: string;
  last_name?: string;
  street?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
}

export interface UserDataPayload {
  sha256_email_address?: string;
  sha256_phone_number?: string;
  email?: string;
  phone_number?: string;
  address?: UserDataAddress;
}

type GtagLike = (...args: unknown[]) => void;

function getGtag(): GtagLike | undefined {
  if (typeof window === "undefined") return undefined;
  const gtag = (window as unknown as { gtag?: GtagLike }).gtag;
  return typeof gtag === "function" ? gtag : undefined;
}

// === NORMALISATION ===================================================
// Google rejects (silently, by never matching) data that is not normalised the
// same way it normalises its own side. Every helper below returns `undefined`
// rather than an empty string so callers can drop the key entirely.

/** Lowercase + trim. Rejects anything without a plausible `@domain.tld`. */
export function normalizeEmail(raw?: string): string | undefined {
  if (!raw) return undefined;
  const value = raw.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return undefined;
  return value;
}

/**
 * E.164: `+` followed by country code and subscriber number, no separators.
 *
 * German visitors type `0170 1234567`, `+49 170 1234567` and `0049170...`
 * interchangeably; all three have to collapse to `+491701234567` or the hash
 * will not match the one Google computed from the advertiser's own records.
 */
export function normalizePhone(raw?: string): string | undefined {
  if (!raw) return undefined;

  const trimmed = raw.trim();
  const hasPlus = trimmed.startsWith("+");
  let digits = trimmed.replace(/\D/g, "");
  if (!digits) return undefined;

  if (!hasPlus) {
    if (digits.startsWith("00")) {
      digits = digits.slice(2); // 0049… → 49…
    } else if (digits.startsWith("0")) {
      digits = `${DEFAULT_PHONE_COUNTRY_CODE}${digits.slice(1)}`; // 0170… → 49170…
    } else {
      // No trunk prefix and no `+`: a bare national number. Anything already
      // long enough to carry a country code is left alone.
      digits = digits.startsWith(DEFAULT_PHONE_COUNTRY_CODE)
        ? digits
        : `${DEFAULT_PHONE_COUNTRY_CODE}${digits}`;
    }
  }

  // E.164 allows 8–15 digits including the country code. Shorter means the
  // visitor typed something that is not a phone number.
  if (digits.length < 8 || digits.length > 15) return undefined;

  return `+${digits}`;
}

/**
 * Everything that is not a letter, space, hyphen or apostrophe.
 *
 * Spelled out as explicit code-point ranges rather than `\p{L}` because
 * `tsconfig.json` targets ES5, where the unicode-property escape (and its
 * required `u` flag) is a compile error. The ranges cover ASCII plus Latin-1
 * Supplement (ß, à–ö, ø–ÿ) and Latin Extended-A (ā–ſ) — i.e. every German,
 * Austrian, Swiss and Central-European name this site actually collects.
 * Applied after `toLowerCase()`, so only the lowercase blocks are needed.
 */
const NON_NAME_CHARACTERS =
  /[^a-zß-öø-ÿĀ-ſ\s'-]/g;

/** Lowercase, collapse whitespace, drop digits and punctuation. */
export function normalizeName(raw?: string): string | undefined {
  if (!raw) return undefined;
  const value = raw
    .trim()
    .toLowerCase()
    .replace(NON_NAME_CHARACTERS, "")
    .replace(/\s+/g, " ")
    .trim();
  return value || undefined;
}

/** First whitespace-delimited token is the given name, the rest the family name. */
export function splitFullName(raw?: string): {
  firstName?: string;
  lastName?: string;
} {
  const normalized = normalizeName(raw);
  if (!normalized) return {};

  const parts = normalized.split(" ");
  if (parts.length === 1) return { firstName: parts[0] };

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function normalizeText(raw?: string): string | undefined {
  if (!raw) return undefined;
  const value = raw.trim().toLowerCase().replace(/\s+/g, " ");
  return value || undefined;
}

function normalizePostalCode(raw?: string): string | undefined {
  if (!raw) return undefined;
  const value = raw.trim().replace(/\s+/g, "").toUpperCase();
  return value || undefined;
}

function normalizeCountry(raw?: string): string | undefined {
  if (!raw) return undefined;
  const value = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(value) ? value : undefined;
}

// === HASHING =========================================================

/** True when Web Crypto is usable (HTTPS or localhost; not plain HTTP). */
export function canHashLocally(): boolean {
  return (
    typeof crypto !== "undefined" &&
    typeof crypto.subtle !== "undefined" &&
    typeof crypto.subtle.digest === "function"
  );
}

/** SHA-256 of `value`, lowercase hex — the encoding Google expects. */
export async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

// === PAYLOAD =========================================================

/**
 * Build the `user_data` object for `gtag('set', 'user_data', …)`.
 *
 * Hashes locally when Web Crypto is available. On the fallback path (plain
 * HTTP, e.g. a LAN preview) the normalised plaintext is emitted instead —
 * gtag.js then hashes it before transmission, so the wire format is identical
 * either way; only `dataLayer` sees the difference.
 *
 * `address` is emitted only when there is something to put in it: Google
 * requires at least one of email / phone / address, and a half-empty address
 * object lowers the match rate rather than raising it.
 *
 * @returns `null` when the input carries no usable identifier at all.
 */
export async function buildUserData(
  input: EnhancedConversionInput
): Promise<UserDataPayload | null> {
  const email = normalizeEmail(input.email);
  const phone = normalizePhone(input.phone);

  const fromFullName = splitFullName(input.name);
  const firstName = normalizeName(input.firstName) ?? fromFullName.firstName;
  const lastName = normalizeName(input.lastName) ?? fromFullName.lastName;

  const street = normalizeText(input.street);
  const city = normalizeText(input.city);
  const region = normalizeText(input.region);
  const postalCode = normalizePostalCode(input.postalCode);
  const hasAddressPart = Boolean(
    firstName || lastName || street || city || region || postalCode
  );
  const country = hasAddressPart
    ? normalizeCountry(input.country) ?? DEFAULT_COUNTRY
    : undefined;

  if (!email && !phone && !hasAddressPart) return null;

  const hashing = canHashLocally();
  const payload: UserDataPayload = {};

  if (email) {
    if (hashing) payload.sha256_email_address = await sha256Hex(email);
    else payload.email = email;
  }

  if (phone) {
    if (hashing) payload.sha256_phone_number = await sha256Hex(phone);
    else payload.phone_number = phone;
  }

  if (hasAddressPart) {
    const address: UserDataAddress = {};

    if (firstName) {
      if (hashing) address.sha256_first_name = await sha256Hex(firstName);
      else address.first_name = firstName;
    }
    if (lastName) {
      if (hashing) address.sha256_last_name = await sha256Hex(lastName);
      else address.last_name = lastName;
    }
    // Street/city/region/postcode are never hashed — Google takes these in the
    // clear and hashing them would break matching.
    if (street) address.street = street;
    if (city) address.city = city;
    if (region) address.region = region;
    if (postalCode) address.postal_code = postalCode;
    if (country) address.country = country;

    payload.address = address;
  }

  return payload;
}

// === CONSENT =========================================================

/**
 * Marketing consent — the gate for `ad_storage` / `ad_user_data`.
 *
 * Deliberately not `isGA4Available()`: that one reads the *analytics* flag,
 * which governs GA4, not Ads.
 */
export function hasMarketingConsent(): boolean {
  const consent = readStoredConsent();
  return consent?.marketing === true;
}

// === PUBLIC API ======================================================

/**
 * Attach user-provided data to the tag, to be picked up by the next conversion.
 *
 * Must be awaited *before* firing `gtag('event', 'conversion', …)` — the tag
 * reads whatever `user_data` is set at the moment the conversion fires.
 *
 * @returns `true` when data was attached.
 */
export async function setEnhancedConversionUserData(
  input: EnhancedConversionInput
): Promise<boolean> {
  if (!hasMarketingConsent()) return false;

  const gtag = getGtag();
  if (!gtag) return false;

  try {
    const userData = await buildUserData(input);
    if (!userData) return false;

    gtag("set", "user_data", userData);
    return true;
  } catch (error) {
    // Never let a hashing or normalisation failure swallow the conversion
    // itself — a conversion without enhanced data still counts.
    console.error("[Enhanced Conversions] Could not set user_data:", error);
    return false;
  }
}

const enhancedConversions = {
  buildUserData,
  canHashLocally,
  hasMarketingConsent,
  normalizeEmail,
  normalizeName,
  normalizePhone,
  setEnhancedConversionUserData,
  sha256Hex,
  splitFullName,
};

export default enhancedConversions;
